<script setup lang="ts">
import type { LastGame } from '~/types/last-game';

// Dashboard "Perfil" do Minha Conta V2 (Figma upMg3vmJ82lB1DspMBbRhe,
// nodes 19771:42267 mobile / 19771:52159 desktop): ofertas, estatísticas,
// atalhos, conquistas e jogos recentes. Os saldos ficam no UserV2BalancesBar
// (Shell no desktop; aqui só no mobile).

// Card de opt-in de ofertas: só aparece pra quem está com o marketing
// DESLIGADO (mesma preferência do toggle "Quero receber atualizações..."
// em Dados da conta → userAcceptsMarketing / mkt_accepted_at). Composable
// compartilhado com Header.vue e ScreenAccountData.vue.
const { userEmail, fetchUserProfile } = useUserData();

// "Não exibir mais" precisa sobreviver a F5 e a fechar/abrir o painel — só um
// ref reseta pro valor inicial a cada remonte do componente. Mesmo padrão de
// localStorage cru usado em useOpenModalAfterTime.ts.
const OFFERS_CARD_DISMISSED_KEY = 'user-v2-offers-card-dismissed';

const offersCardVisible = ref(
  !(import.meta.client && localStorage.getItem(OFFERS_CARD_DISMISSED_KEY)),
);

function dismissOffersCard() {
  offersCardVisible.value = false;
  localStorage.setItem(OFFERS_CARD_DISMISSED_KEY, '1');
}

const {
  isActive: userAcceptsMarketing,
  isSaving: isAcceptingOffers,
  setMarketing,
} = useMarketingOptIn();

// "Aceitar ofertas" liga o marketing de verdade (PATCH bff/users/self-mkt,
// mesmo endpoint do toggle em Dados da conta). Com a flag ligada, o card
// some sozinho pelo v-if (!userAcceptsMarketing).
function acceptOffers() {
  setMarketing(true);
}

// Conquistas: prévia (9 primeiras) no dashboard; "Ver mais"/"+" abrem a tela
// Conquistas e clicar numa conquista abre o modal de resgate. A seção inteira
// só existe se o CRM (Smartico) tiver badge pra este jogador — sem badge não
// há o que mostrar, e a tela Conquistas não tem outra entrada além daqui.
const { list: achievementsList, openDetail } = useUserV2Achievements();
const { open: openUserV2Screen, close } = useUserV2Modal();

const previewAchievements = computed(() => achievementsList.value.slice(0, 9));

// Jogos recentes reais — mesma fonte do CasinoLastGamesPlayed (useLastGames
// lê do cache local, populado no login/home e a cada jogo jogado via
// addGame). Renderizamos com o card do design V2, mas os dados/rota são os
// últimos jogos de verdade.
const { getLastGames, hasGames, onLastGamesUpdate } = useLastGames();

const { getResponsiveThumbnailSrc } = useResponsiveThumbnail();
const { validateGameUrl } = useGamesWinners();
const { isAuthenticated } = storeToRefs(useAuthStore());

const recentGames = ref<LastGame[]>([]);

// mesmo corte de 10 usado nos outros consumidores deste cache
// (LastGamesPlayed.vue, LastPlayedPreview.vue) — o endpoint em si não pagina.
const RECENT_GAMES_LIMIT = 10;
const displayedRecentGames = computed(() =>
  recentGames.value.slice(0, RECENT_GAMES_LIMIT),
);

function loadRecentGames() {
  recentGames.value = isAuthenticated.value && hasGames() ? getLastGames() : [];
}

let unsubscribeRecentGames: (() => void) | null = null;

onBeforeMount(() => {
  // garante a preferência de marketing (mkt_accepted_at) pro card de ofertas
  if (!userEmail.value) {
    fetchUserProfile({ force: true });
  }
});

onMounted(() => {
  unsubscribeRecentGames = onLastGamesUpdate(loadRecentGames);
  loadRecentGames();
});

onBeforeUnmount(() => {
  unsubscribeRecentGames?.();
  unsubscribeRecentGames = null;
});

watch(isAuthenticated, loadRecentGames);
</script>

<template>
  <!-- mobile: saldos no topo do Perfil (no desktop vêm sempre do Shell) -->
  <div class="md:hidden">
    <UserV2BalancesBar />
  </div>

  <!-- Atalhos: no mobile ficam logo abaixo dos saldos, antes do card de
       ofertas e de Estatísticas (pedido de reposicionamento). No desktop a
       mesma seção permanece na posição original, depois de Estatísticas
       (ver segunda instância abaixo) — duas instâncias com display
       alternado por breakpoint em vez de reordenar via flex `order`, que
       não alcançaria Atalhos por estar aninhado dentro do card com borda
       lá embaixo. -->
  <UserV2Shortcuts class="flex md:hidden" />

  <!-- desktop: título "Seu Perfil" da coluna direita -->
  <p class="hidden text-[12px] font-medium leading-[16px] text-white md:block">
    Seu Perfil
  </p>

  <!-- Card de opt-in de ofertas (Figma node 23279:87696 desktop /
       23287:88053 mobile — fundo, borda, raio, banner e texto diferem
       entre os dois) -->
  <div
    v-if="!userAcceptsMarketing && offersCardVisible"
    class="border-solid relative flex w-full flex-col gap-[10px] rounded-[16px] border border-white/10 bg-gradient-to-b from-[#050c1f] to-[#08112d] px-[12px] pb-[12px] md:rounded-[24px] md:border-v2-default/40 md:to-black"
  >
    <div class="flex flex-col gap-[3px]">
      <!-- Banner "NIVEIS" + gemas dos elos: arte estática (igual pra todo
           mundo, não reflete o elo real do jogador), então vem como uma
           imagem só em vez de recriar as máscaras/blend do Figma. Sem
           padding-top no card (ver acima): a imagem começa colada na borda.
           `aspect-ratio` via style (não a utility `aspect-*` do Tailwind — o
           plugin
           @tailwindcss/aspect-ratio mata as classes nomeadas do core, ver
           comentário em components/levels/TierPodium.vue) trava a proporção
           real do asset: a altura acompanha a largura de verdade do card, sem
           cortar nem sobrar espaço em nenhuma largura de painel. Composições
           mobile/desktop diferentes (não é só escala), duas imagens. -->
      <!-- object-contain: se a arte for trocada por outra proporção sem
           atualizar o aspect-ratio, sobra faixa transparente em vez de
           esticar a imagem -->
      <img
        alt=""
        class="w-full object-contain md:hidden"
        :src="
          getAssetsUrlForCDN(
            '/images/user-v2/notifications-levels-banner-mobile.png',
          )
        "
        style="aspect-ratio: 1011 / 377"
      />
      <img
        alt=""
        class="hidden w-full object-contain md:block"
        :src="
          getAssetsUrlForCDN(
            '/images/user-v2/notifications-levels-banner-v2.png',
          )
        "
        style="aspect-ratio: 544 / 203"
      />
      <p
        class="text-center text-[14px] font-bold leading-[20px] text-v2-foreground md:hidden"
      >
        Ative as notificações para Ganhar XP de nível.
      </p>
      <p
        class="hidden text-center text-[14px] leading-[20px] text-v2-foreground md:block"
      >
        Ative as notificações para
        <span class="font-bold">Ganhar XP e progredir de nível.</span>
      </p>
      <p class="text-center text-[9px] leading-[1.4] text-white/20">
        *Estou ciente que posso cancelar o recebimento a qualquer momento.
      </p>
    </div>

    <button
      class="flex h-[32px] w-full items-center justify-center rounded-[12px] bg-v2-success px-[12px] text-[12px] font-bold leading-[16px] text-white disabled:opacity-60"
      :disabled="isAcceptingOffers"
      @click="acceptOffers"
    >
      {{ isAcceptingOffers ? 'Ativando...' : 'Ativar notificações' }}
    </button>

    <!-- X removido (mobile e desktop) — dispensar-pra-sempre agora é só por
         aqui, nos dois breakpoints. -->
    <button
      class="self-center text-[12px] leading-[16px] text-white/40"
      @click="dismissOffersCard"
    >
      Não exibir mais
    </button>
  </div>

  <!-- Estatísticas, Atalhos, Conquistas e Jogos recentes: no mobile cada
         seção fica solta, sem moldura externa (node 19771:42294 — só a
         seção Estatísticas já tem borda própria, linha abaixo); a borda
         envolvendo tudo num único card é só da coluna direita do desktop
         (19771:52182) -->
  <div
    class="border-solid flex w-full flex-col items-start gap-[10px] md:rounded-[24px] md:border md:border-v2-default/40 md:p-[16px]"
  >
    <UserV2Stats />

    <!-- Atalhos (desktop): posição original, ver instância mobile lá em
         cima logo após os saldos -->
    <UserV2Shortcuts class="hidden md:flex" />

    <!-- Conquistas -->
    <section
      v-if="achievementsList.length"
      class="flex w-full flex-col gap-[10px]"
    >
      <div
        class="flex h-[32px] w-full items-center justify-between rounded-[12px] bg-v2-default-300 pl-[12px]"
      >
        <p
          class="text-[14px] font-medium leading-[16px] text-white md:text-[12px]"
        >
          Conquistas
        </p>
        <button
          class="flex h-[32px] items-center gap-[8px] rounded-[12px] px-[12px] text-[14px] leading-[16px] text-v2-cyan-600 md:text-[12px]"
          type="button"
          @click="openUserV2Screen('achievements')"
        >
          Ver mais
          <svg
            class="size-[16px]"
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            viewBox="0 0 20 20"
          >
            <path d="M4 10h12m0 0-4-4m4 4-4 4" />
          </svg>
        </button>
      </div>

      <!-- justify-center só no mobile (não justify-start): distribui a
           sobra de espaço pros dois lados em vez de largar tudo à direita —
           tanto nas linhas cheias quanto na última linha incompleta
           (flex-wrap aplica justify-content por linha, diferente de Grid).
           Bolinha com size-[67px] fixo, sem depender do tamanho da coluna,
           então não deforma em nenhuma largura. No desktop (coluna fixa do
           Shell) esse espaço não ocorre, então mantém o alinhamento
           original à esquerda. -->
      <div
        class="flex w-full flex-wrap justify-center gap-[6px] md:justify-start"
      >
        <button
          v-for="achievement in previewAchievements"
          :key="achievement.key"
          class="flex size-[67px] shrink-0 items-center justify-center rounded-full border border-v2-default-500 bg-v2-default-300"
          type="button"
          @click="openDetail(achievement.key)"
        >
          <img
            alt=""
            class="size-[40px] object-contain"
            :src="achievement.icon"
          />
        </button>
        <button
          class="flex size-[67px] shrink-0 items-center justify-center rounded-full border border-v2-default-500 bg-v2-default-300"
          type="button"
          @click="openUserV2Screen('achievements')"
        >
          <img
            alt=""
            class="size-[44px] object-contain"
            :src="
              getAssetsUrlForCDN('/images/user-v2/achievements/ach-plus.svg')
            "
          />
        </button>
      </div>
    </section>

    <!-- Jogos recentes -->
    <section class="flex w-full flex-col gap-[10px]">
      <div
        class="flex h-[32px] w-full items-center rounded-[12px] bg-v2-default-300 pl-[12px]"
      >
        <p
          class="text-[14px] font-medium leading-[16px] text-white md:text-[12px]"
        >
          Jogos recentes
        </p>
      </div>

      <div
        v-if="displayedRecentGames.length"
        class="no-scrollbar flex w-full gap-[6px] overflow-x-auto"
      >
        <NuxtLink
          v-for="game in displayedRecentGames"
          :key="game.game_id"
          class="relative h-[106px] w-[91px] shrink-0 overflow-clip rounded-[6px] bg-v2-default-300"
          :to="validateGameUrl(game.slug)"
          @click="close()"
        >
          <img
            v-if="game.image && game.image !== '0'"
            :alt="game.game_name"
            class="size-full object-cover"
            loading="lazy"
            :src="getResponsiveThumbnailSrc(game.image)"
          />
          <span
            v-else
            class="flex size-full items-center justify-center px-[6px] text-center text-[10px] font-bold leading-[1.2] text-white"
          >
            {{ game.game_name }}
          </span>
        </NuxtLink>
      </div>
      <p
        v-else
        class="rounded-[10px] bg-v2-default/40 p-[16px] text-center text-[12px] leading-[16px] text-v2-default-800"
      >
        Você ainda não tem jogos recentes.
      </p>
    </section>
  </div>
</template>

<style scoped>
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
